/**
 * Onboarding UI Types
 *
 * UI-ONLY types for the onboarding wizard.
 * All CoPilot API types come from @order-up/models
 */

import type { IMerchant } from '@order-up/models';
import { DEFAULT_OPERATING_HOURS } from '@order-up/common';

// =============================================================================
// APPLICATION DATA EXTENSION
// =============================================================================

/**
 * Onboarding-specific extension of the CoPilot `IMerchant` shape.
 *
 * The base `IMerchant` comes from the CoPilot API contract in
 * `@order-up/models` and does NOT have a dedicated federal tax ID field.
 * OrderUp's onboarding flow still needs to collect the EIN for non-sole-
 * proprietor businesses (see `BusinessTypeStep` — "you'll need your EIN"),
 * so we carry it on an extension type that lives only inside the
 * onboarding app.
 *
 * The Supabase `onboarding_applications.application_data` column is
 * JSONB, so additional keys persist cleanly without a database migration.
 * The onboarding server service does NOT validate `application_data`
 * through the strict CoPilot Zod schema, so the key survives the full
 * save/load round-trip.
 *
 * When CoPilot integration lands and you learn where EIN actually maps
 * to in the CoPilot Merchant PUT payload (likely a `customFields` entry
 * or a new top-level field CoPilot adds), you can rename / remove this
 * extension at the form edge without touching `packages/models`.
 */
export interface OnboardingApplicationData extends IMerchant {
    /**
     * Federal Tax ID / Employer Identification Number (EIN).
     * 9-digit number, stored in the canonical `XX-XXXXXXX` format
     * produced by `lib/formatters.ts → formatEIN`.
     *
     * Required for all non-sole-proprietor business types (LLC, Corp,
     * Partnership, LLP, Non-Profit). Sole proprietors use their SSN
     * instead (stored on `ownership.owner.ownerSSN`).
     */
    federalTaxId?: string;

    /**
     * Restaurant-onboarding fields collected by the wizard so
     * `OnboardingApplicationService.provision()` can build a fully-typed
     * `CreateRestaurantInput` and pass it to `restaurantService.createRestaurant`
     * without a `cast as any`. Pre-W6 the form only captured legal name +
     * business phone; the strict Zod `RestaurantSchema` rejected the
     * resulting create with missing-field errors and the user ended up with
     * a tenant but no restaurant.
     *
     * The Supabase `onboarding_applications.application_data` column is
     * JSONB, so adding this nested object persists cleanly without a
     * database migration.
     */
    restaurant?: LocationOnboardingData;

    /**
     * ISO-3166 alpha-2 country code for `demographic.businessAddress`. Defaults
     * to `'US'` because every existing US-state-only address question in
     * `BusinessInfoStep` implicitly assumed US. Captured here (rather than
     * extending CoPilot's `IAddress` shape) because the CoPilot vendor model
     * doesn't carry country.
     */
    businessAddressCountry?: string;
}

/**
 * Restaurant-specific data collected during client-onboarding. Maps 1:1 onto
 * the strict-Zod restaurant fields that `restaurantService.createRestaurant`
 * requires beyond what `IMerchant` already provides.
 */
export interface LocationOnboardingData {
    /**
     * Restaurant concept (matches `Restaurant.concept`). Sourced from the
     * admin-managed `business-types` collection via `useGetBusinessTypes`,
     * with the optional second-level pick from `business_sub_categories`
     * via `useGetBusinessSubCategoriesByType` overwriting it.
     *
     * The value stored is the chosen business-type or sub-category's
     * display name (e.g. `'Pizza Restaurant'`) — same convention as the
     * admin `RestaurantModal`. The FK back to `business_types` is
     * preserved separately via `businessTypeGuid`.
     */
    concept?: string;

    /**
     * FK to the admin-managed `business_types` collection. Set when the
     * user picks a business type; preserved through the patch even when
     * `concept` is later overwritten by a sub-category name. Mirrors
     * `Restaurant.businessTypeGuid` (optional). Empty string is treated
     * the same as undefined by `buildRestaurantCreateInput`.
     */
    businessTypeGuid?: string;

    /**
     * Hours-mode discriminator. `'fixed'` (the default) means the operator
     * filled in the 7-day schedule; `'by_appointment'` means the merchant
     * doesn't have fixed hours (contractors, on-call, B2B). When
     * `'by_appointment'`, the wizard still seeds `operatingHours` with
     * defaults underneath so a switch back to `'fixed'` doesn't lose data.
     * Mirrors `Restaurant.hoursMode`.
     */
    hoursMode?: 'fixed' | 'by_appointment';

    /**
     * Public-facing email for the restaurant. Distinct from
     * `ownership.owner.ownerEmail` so listings, receipts, and customer-
     * facing surfaces don't expose the owner's personal email. Defaults to
     * the owner email via the "Use my email" toggle in
     * `LocationDetailsStep`.
     */
    contactEmail?: string;

    /**
     * Operating hours per day. Shape matches `DayHoursSchema`:
     *   `{ open: 'HH:mm', close: 'HH:mm', closed: boolean }`.
     * Initial seed copies `DEFAULT_OPERATING_HOURS` from `@order-up/common`.
     */
    operatingHours?: Record<string, { open: string; close: string; closed: boolean }>;
}

/**
 * Default `LocationOnboardingData` for a brand-new draft. Mirrors the
 * shared `DEFAULT_OPERATING_HOURS` so the operator can submit without
 * touching the hours editor for a standard 9-22/9-23/10-21 schedule.
 */
export const DEFAULT_LOCATION_ONBOARDING_DATA: LocationOnboardingData = {
    concept: '',
    businessTypeGuid: '',
    hoursMode: 'fixed',
    contactEmail: '',
    operatingHours: { ...DEFAULT_OPERATING_HOURS },
};

// =============================================================================
// STEP CONFIGURATION (UI-specific)
// =============================================================================

export type OnboardingStep =
    | 'business-type'        // Step 1: What type of business?
    | 'personal-info'        // Step 2: About you (the applicant)
    | 'business-info'        // Step 3: About your business
    | 'restaurant-details'   // Step 4: Restaurant concept, contact, hours
    | 'owners'               // Step 5: Additional owners (25%+)
    | 'review';              // Step 6: Review and submit

export const STEP_ORDER: OnboardingStep[] = [
    'business-type',
    'personal-info',
    'business-info',
    'restaurant-details',
    'owners',
    'review'
];

export interface StepConfig {
    label: string;
    shortLabel: string;
    description: string;
    icon: string;
    skipIf?: 'sole_proprietor'; // Skip this step for sole props
}

export const STEP_CONFIG: Record<OnboardingStep, StepConfig> = {
    'business-type': {
        label: 'Business Type',
        shortLabel: 'Type',
        description: 'What type of business do you have?',
        icon: 'briefcase'
    },
    'personal-info': {
        label: 'Personal Info',
        shortLabel: 'Personal',
        description: 'Tell us a bit about yourself',
        icon: 'user'
    },
    'business-info': {
        label: 'Business Info',
        shortLabel: 'Business',
        description: 'Details about your business',
        icon: 'building'
    },
    'restaurant-details': {
        label: 'Restaurant Details',
        shortLabel: 'Restaurant',
        description: 'Concept, public contact, and operating hours',
        icon: 'utensils-crossed'
    },
    owners: {
        label: 'Ownership',
        shortLabel: 'Owners',
        description: 'Additional owners with 25%+ ownership',
        icon: 'users',
        skipIf: 'sole_proprietor'
    },
    review: {
        label: 'Review & Submit',
        shortLabel: 'Review',
        description: 'Review your application before submitting',
        icon: 'check-circle'
    }
};

// =============================================================================
// UI DROPDOWN OPTIONS
// =============================================================================

export const OWNERSHIP_TYPE_OPTIONS = [
    { value: 'SOLE_PROP', label: 'Sole Proprietorship' },
    { value: 'LLC', label: 'Limited Liability Company (LLC)' },
    { value: 'CORP', label: 'Corporation' },
    { value: 'S-CORP', label: 'S Corporation' },
    { value: 'C-CORP', label: 'C Corporation' },
    { value: 'PARTNERSHIP', label: 'Partnership' },
    { value: 'LLP', label: 'Limited Liability Partnership' },
    { value: 'NON_PROFIT', label: 'Non-Profit Organization' }
] as const;

export const TAX_FILING_OPTIONS = [
    { value: 'EIN', label: 'EIN (Employer Identification Number)' },
    { value: 'SSN', label: 'SSN (Social Security Number)' }
] as const;

export const BANK_ACCOUNT_TYPE_OPTIONS = [
    { value: 'CHECKING', label: 'Checking' },
    { value: 'SAVINGS', label: 'Savings' }
] as const;

export const OWNER_TITLE_OPTIONS = [
    { value: 'CEO', label: 'CEO' },
    { value: 'CFO', label: 'CFO' },
    { value: 'COO', label: 'COO' },
    { value: 'PRESIDENT', label: 'President' },
    { value: 'VICE_PRESIDENT', label: 'Vice President' },
    { value: 'OWNER', label: 'Owner' },
    { value: 'PARTNER', label: 'Partner' },
    { value: 'MEMBER', label: 'Member' },
    { value: 'DIRECTOR', label: 'Director' },
    { value: 'SECRETARY', label: 'Secretary' },
    { value: 'TREASURER', label: 'Treasurer' },
    { value: 'OTHER', label: 'Other' }
] as const;

export const TIMEZONE_OPTIONS = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' }
] as const;

export const MONTHLY_VOLUME_RANGES = [
    { value: 5000, label: '<$5K' },
    { value: 10000, label: '$5K-$10K' },
    { value: 25000, label: '$10K-$25K' },
    { value: 50000, label: '$25K-$50K' },
    { value: 100000, label: '$50K-$100K' },
    { value: 250000, label: '$100K-$250K' },
    { value: 500000, label: '$250K-$500K' },
    { value: 1000000, label: '$500K+' }
] as const;

export const AVERAGE_TICKET_RANGES = [
    { value: 15, label: '<$15' },
    { value: 25, label: '$15-$25' },
    { value: 50, label: '$25-$50' },
    { value: 100, label: '$50-$100' },
    { value: 250, label: '$100-$250' },
    { value: 500, label: '$250+' }
] as const;

export const RESTAURANT_MCC_CODES = [
    { value: '5812', label: '5812 - Eating Places, Restaurants' },
    { value: '5813', label: '5813 - Drinking Places (Bars, Taverns)' },
    { value: '5814', label: '5814 - Fast Food Restaurants' }
] as const;

// US States for dropdowns
export const US_STATES = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
    { value: 'DC', label: 'District of Columbia' }
] as const;
